import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsSearchOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-100/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.3)]'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
            <i className="ri-film-line text-xl text-white"></i>
          </div>
          <span className="font-heading text-xl font-bold text-foreground-950 tracking-wide">
            四虎影院
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {categories.map((cat) => {
            const isActive = location.pathname === `/category/${cat.slug}`;
            return (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                  isActive
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            );
          })}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70 transition-colors cursor-pointer"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </button>
          <Link
            to="/about"
            className="whitespace-nowrap text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
          >
            关于我们
          </Link>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <button
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </button>
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-lg ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {isSearchOpen && (
        <div className="bg-background-100 border-t border-background-200/50 px-4 py-3">
          <form onSubmit={handleSearch} className="max-w-[1400px] mx-auto flex items-center gap-2">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索电影、电视剧、综艺、动漫..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-background-200 text-sm text-foreground-900 placeholder-foreground-400 border border-background-300 focus:outline-none focus:border-primary-500 transition-colors"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {isMobileOpen && (
        <div className="md:hidden bg-background-100 border-t border-background-200/50">
          <div className="px-4 py-3 flex flex-col gap-1">
            {categories.map((cat) => {
              const isActive = location.pathname === `/category/${cat.slug}`;
              return (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-primary-500/20 text-primary-500'
                      : 'text-foreground-700 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              );
            })}
            <Link
              to="/about"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className="ri-information-line text-base"></i>
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}